import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIsField } from '../lib/media.js'
import { useSession } from '../lib/session.js'
import { useTRPC } from '../lib/trpc.js'
import { LATEST_RELEASE_ID, unseenReleases } from './releases.js'
import { Spotlight } from './Spotlight.js'
import { Tour, type TourSource } from './Tour.js'
import { tourFor } from './tours.js'
import type { ReleaseNote, TourStep } from './types.js'
import { Welcome } from './Welcome.js'
import { WhatsNewToast } from './WhatsNewToast.js'

/**
 * The in-app guide, for the whole signed-in app. Decision 115.
 *
 * It decides three things on its own, in this order, and never two at once:
 *
 * 1. **The welcome.** Offered to anybody who has neither finished the tour nor
 *    turned it off. "Not now" asks again after a while on this device; "Don't
 *    show this again" is remembered on the server, so it holds on every device,
 *    until Settings → Your account takes the tour again.
 * 2. **What's new.** A quiet card, once the welcome is out of the way, when a
 *    release note applies to them that they have not read.
 * 3. Nothing. Help is always there on `?`, the Help button and Me.
 *
 * Everything heavy — the articles, the help panel, the What's new window — is
 * loaded the first time it is opened, so a technician's phone does not carry
 * forty articles to show a job.
 */

const HelpPanel = lazy(() => import('./HelpPanel.js').then((module) => ({ default: module.HelpPanel })))
const WhatsNewDialog = lazy(() =>
  import('./WhatsNewDialog.js').then((module) => ({ default: module.WhatsNewDialog })),
)
// Reporting a bug (decision 117) brings the screenshot code with it, so it
// loads only when somebody reports one.
const BugReporter = lazy(() => import('../bugs/BugReporter.js').then((module) => ({ default: module.BugReporter })))

export interface GuideState {
  tourCompletedAt: Date | string | null
  tourDismissedAt: Date | string | null
  releasesSeenThrough: string | null
}

interface GuideValue {
  /** False outside the signed-in frame, where none of this is drawn. */
  available: boolean
  state: GuideState | null
  helpOpen: boolean
  openHelp: (articleId?: string) => void
  closeHelp: () => void
  toggleHelp: () => void
  startTour: () => void
  /** Turns the welcome back on, so it offers itself again as it did the first time. */
  resetTour: () => void
  resetting: boolean
  unseen: ReleaseNote[]
  openWhatsNew: () => void
  markReleasesSeen: () => void
  /** Goes to a screen and, with a selector, lights up the thing on it. */
  showMe: (to: string, spotlight?: string, label?: string) => void
  /** Closes help and starts a bug report over the screen in view. Decision 117. */
  reportBug: () => void
  /** Their reports waiting on them (a fix to check, a question), theirs open, and new ones to look at. */
  bugs: { waitingOnYou: number; mine: number; open: number }
}

const noop = () => undefined

const GuideContext = createContext<GuideValue>({
  available: false,
  state: null,
  helpOpen: false,
  openHelp: noop,
  closeHelp: noop,
  toggleHelp: noop,
  startTour: noop,
  resetTour: noop,
  resetting: false,
  unseen: [],
  openWhatsNew: noop,
  markReleasesSeen: noop,
  showMe: noop,
  reportBug: noop,
  bugs: { waitingOnYou: 0, mine: 0, open: 0 },
})

export const useGuide = (): GuideValue => useContext(GuideContext)

/** How long "Not now" holds on this device before the welcome asks again. */
const SNOOZE_MS = 12 * 60 * 60 * 1000

/** Long enough for the screen under it to settle, so the welcome is not the first flash. */
const SETTLE_MS = 700

function snoozeKey(userId: string) {
  return `fieldops.guide.welcomeSnoozedUntil.${userId}`
}

function readSnooze(userId: string): number {
  try {
    return Number(window.localStorage.getItem(snoozeKey(userId)) ?? 0)
  } catch {
    return 0
  }
}

function writeSnooze(userId: string, until: number) {
  try {
    window.localStorage.setItem(snoozeKey(userId), String(until))
  } catch {
    // Private browsing. The welcome asks again on the next load, which is all
    // "Not now" promised.
  }
}

/** Typing in a box, where `?` is a character and not a request for help. */
function typing(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}

interface RunningTour {
  steps: TourStep[]
  source: TourSource
  /** Where they were, to put them back when it ends. */
  origin: string
}

export function GuideProvider({ children }: { children: ReactNode }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const field = useIsField()
  const { user, can } = useSession()

  const guide = useQuery({
    ...trpc.guide.state.queryOptions(),
    enabled: Boolean(user),
    staleTime: 5 * 60_000,
    retry: 1,
  })
  const state = guide.data ?? null

  const store = (next: GuideState) => queryClient.setQueryData(trpc.guide.state.queryKey(), next as never)
  const finish = useMutation(trpc.guide.finishTour.mutationOptions({ onSuccess: store }))
  const dismiss = useMutation(trpc.guide.dismissTour.mutationOptions({ onSuccess: store }))
  const reset = useMutation(trpc.guide.resetTour.mutationOptions({ onSuccess: store }))
  const seeReleases = useMutation(trpc.guide.seeReleases.mutationOptions({ onSuccess: store }))

  const [helpOpen, setHelpOpen] = useState(false)
  const [helpArticle, setHelpArticle] = useState<string | null>(null)
  const [tour, setTour] = useState<RunningTour | null>(null)
  const [whatsNewOpen, setWhatsNewOpen] = useState(false)
  const [toastClosed, setToastClosed] = useState(false)
  const [snoozedUntil, setSnoozedUntil] = useState(() => (user ? readSnooze(user.id) : 0))
  const [settled, setSettled] = useState(false)
  const [spot, setSpot] = useState<{ selector: string; label: string } | null>(null)
  const [reporting, setReporting] = useState(false)

  // Nothing broadcasts a bug's change, so this is read on focus and now and then.
  const bugCounts = useQuery({
    ...trpc.bugs.counts.queryOptions(),
    enabled: Boolean(user),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60_000,
    retry: 1,
  })
  const bugs = bugCounts.data ?? { waitingOnYou: 0, mine: 0, open: 0 }

  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(true), SETTLE_MS)
    return () => window.clearTimeout(timer)
  }, [])

  const reader = useMemo(() => ({ can, field }), [can, field])
  const unseen = useMemo(
    () => (state ? unseenReleases(state.releasesSeenThrough, reader) : []),
    [state, reader],
  )

  // A link straight to a job lands on the job, not on a welcome over it.
  const jobOpen = new URLSearchParams(location.search).has('jobId')
  const offerWelcome =
    settled &&
    Boolean(state) &&
    !state?.tourCompletedAt &&
    !state?.tourDismissedAt &&
    snoozedUntil < Date.now() &&
    !tour &&
    !jobOpen

  const showToast =
    settled && Boolean(state) && !offerWelcome && !tour && !whatsNewOpen && !toastClosed && unseen.length > 0

  const openHelp = useCallback((articleId?: string) => {
    setHelpArticle(articleId ?? null)
    setHelpOpen(true)
  }, [])
  const closeHelp = useCallback(() => setHelpOpen(false), [])
  const toggleHelp = useCallback(() => {
    setHelpArticle(null)
    setHelpOpen((was) => !was)
  }, [])

  const startTour = useCallback(
    (source: TourSource = 'manual') => {
      setHelpOpen(false)
      setWhatsNewOpen(false)
      setSpot(null)
      setTour({
        steps: tourFor(field, can),
        source,
        origin: `${location.pathname}${location.search}`,
      })
    },
    [field, can, location.pathname, location.search],
  )

  const endTour = (how: 'finished' | 'later' | 'never' | 'stopped') => {
    const origin = tour?.origin
    setTour(null)
    const through = LATEST_RELEASE_ID ? { releasesThrough: LATEST_RELEASE_ID } : {}
    if (how === 'finished') finish.mutate(through)
    if (how === 'never') dismiss.mutate(through)
    if (how === 'later' && user) {
      const until = Date.now() + SNOOZE_MS
      writeSnooze(user.id, until)
      setSnoozedUntil(until)
    }
    if (origin && origin !== `${location.pathname}${location.search}`) navigate(origin)
  }

  const markReleasesSeen = useCallback(() => {
    setToastClosed(true)
    if (LATEST_RELEASE_ID && unseen.length > 0) seeReleases.mutate({ through: LATEST_RELEASE_ID })
  }, [seeReleases, unseen.length])

  const showMe = useCallback(
    (to: string, selector?: string, label?: string) => {
      setHelpOpen(false)
      setWhatsNewOpen(false)
      if (`${location.pathname}${location.search}` !== to) navigate(to)
      setSpot(selector ? { selector, label: label ?? 'This one' } : null)
    },
    [navigate, location.pathname, location.search],
  )

  // `?` opens help from anywhere a person is not typing.
  useEffect(() => {
    if (!user) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '?' || event.metaKey || event.ctrlKey || event.altKey) return
      if (typing(event.target) || tour || reporting) return
      event.preventDefault()
      toggleHelp()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [user, tour, reporting, toggleHelp])

  const value = useMemo<GuideValue>(
    () => ({
      available: true,
      state,
      helpOpen,
      openHelp,
      closeHelp,
      toggleHelp,
      startTour: () => startTour('manual'),
      resetTour: () => reset.mutate(),
      resetting: reset.isPending,
      unseen,
      openWhatsNew: () => {
        setHelpOpen(false)
        setWhatsNewOpen(true)
      },
      markReleasesSeen,
      showMe,
      reportBug: () => {
        setHelpOpen(false)
        setWhatsNewOpen(false)
        setSpot(null)
        setReporting(true)
      },
      bugs,
    }),
    [state, helpOpen, openHelp, closeHelp, toggleHelp, startTour, reset, unseen, markReleasesSeen, showMe, bugs],
  )

  return (
    <GuideContext.Provider value={value}>
      {children}

      {offerWelcome && user ? (
        <Welcome
          name={user.name}
          field={field}
          onStart={() => startTour('welcome')}
          onLater={() => endTour('later')}
          onNever={() => endTour('never')}
        />
      ) : null}

      {tour ? (
        <Tour
          steps={tour.steps}
          source={tour.source}
          field={field}
          onFinish={() => endTour('finished')}
          onLater={() => endTour('later')}
          onNever={() => endTour('never')}
          onStop={() => endTour('stopped')}
        />
      ) : null}

      {spot && !tour ? (
        <Spotlight
          selector={spot.selector}
          label={spot.label}
          field={field}
          onDone={() => setSpot(null)}
        />
      ) : null}

      {showToast ? (
        <WhatsNewToast
          release={unseen[0]!}
          changes={unseen.reduce((sum, release) => sum + release.items.length, 0)}
          field={field}
          onOpen={() => setWhatsNewOpen(true)}
          onLater={() => setToastClosed(true)}
          onDismiss={markReleasesSeen}
        />
      ) : null}

      {whatsNewOpen ? (
        <Suspense fallback={null}>
          <WhatsNewDialog
            releases={unseen.length > 0 ? unseen : []}
            onClose={() => {
              setWhatsNewOpen(false)
              markReleasesSeen()
            }}
          />
        </Suspense>
      ) : null}

      {reporting ? (
        <Suspense fallback={null}>
          <BugReporter
            field={field}
            onClose={() => setReporting(false)}
            onOpenReport={(number) => navigate(`/help/bugs/${number}`)}
          />
        </Suspense>
      ) : null}

      {helpOpen ? (
        <Suspense fallback={null}>
          <HelpPanel
            initialArticle={helpArticle}
            field={field}
            onClose={closeHelp}
          />
        </Suspense>
      ) : null}
    </GuideContext.Provider>
  )
}
